<template>
  <resize-observer :breakpoints="breakpoints">
    <collapsible-panel
      class="c-wro-a-p"
      :class="{ 'c-wro-a-p--collapsed': !active }"
      :collapse="!active"
      variation="bare"
      slot-scope="{ el }"
    >
      <div ref="header" class="c-wro-a-p__header" slot="header">
        <div class="c-wro-a-p__icon" v-if="complete">
          <circle-checkmark-icon variation="complete" />
        </div>
        <div v-else class="c-wro-a-p__icon">
          <circle-checkmark-icon/>
        </div>
        <h3 :class="titleClass('c-wro-a-p__title', el.is)">{{ headerText }}</h3>
        <div class="c-wro-a-p__toggle">
          <sb-button :disabled="disableEdit" @click.prevent="edit" size="small" variation="minimal" v-if="!active">{{ isNotStep3WroEdit ? 'Edit' : 'View Only' }}</sb-button>
        </div>
      </div>
      <div :class="breakpointClass('c-wro-a-p__body', el.is)">
        <p :class="breakpointClass('c-wro-a-p__step', el.is)">{{ $t('actionablePanel.steps', { currentStep, totalSteps }) }}</p>
        <div :class="breakpointClass('c-wro-a-p__info', el.is)" :style="infoStyles" slot="info">
          <slot name="info" />
        </div>
        <div :class="breakpointClass('c-wro-a-p__content', el.is)">
          <slot />
        </div>
        <div :class="breakpointClass('c-wro-a-p__table', el.is)">
          <slot name="table" />
        </div>
        <div :class="breakpointClass('c-wro-a-p__footer', el.is)" slot="footer">
          <slot name="footer"/>
        </div>
      </div>
    </collapsible-panel>
  </resize-observer>
</template>

<script>
import { throttle } from 'lodash'
import CollapsiblePanel from './CollapsiblePanel'

export default {
  name: 'ActionablePanel',
  components: {
    CollapsiblePanel
  },
  mixins: [],
  props: {
    headerText: {
      type: String,
      required: true
    },
    currentStep: {},
    totalSteps: {},
    complete: {},
    active: {},
    disableEdit: {
      type: Boolean,
      default: true
    },
    isNotStep3WroEdit: {
      type: Boolean,
      default: true
    },
    breakpoints: {
      type: Object,
      default: () => ({
        wrap: el => el.width < 1100,
        full: el => el.width >= 1100
      })
    }
  },
  data: () => ({
    headerHeight: null,
    wrap: false
  }),
  mounted () {
    this.handleResize()
    window.addEventListener('resize', throttle(this.handleResize, 500))
  },
  destroyed () {
    window.removeEventListener('resize', throttle(this.handleResize, 500))
  },
  watch: {},
  computed: {
    infoStyles () {
      // the hardcoded rem's correspond to the amount of gap in the grid.
      const marginTop = this.wrap ? '0px' : `calc(-${this.headerHeight}px + 2rem)`
      return {
        marginTop
      }
    }
  },
  methods: {
    edit () {
      this.$emit('edit')
    },
    titleClass (baseClass, size) {
      if (!this.active) return { [baseClass]: true }
      return this.breakpointClass(baseClass, size)
    },
    breakpointClass (baseClass, size) {
      this.wrap = size.wrap
      return Object.keys(size).reduce((acc, n) => {
        acc[baseClass + '--' + n] = size[n]
        return acc
      }, {
        [baseClass]: true
      })
    },
    handleResize () {
      const ref = this.$refs.header
      if (ref) this.headerHeight = ref.offsetHeight
    }
  },
  directives: {},
  filters: {},
  i18n: {
    messages: {
      'en-US': {
        actionablePanel: {
          steps: 'Step {currentStep} of {totalSteps}'
        }
      }
    }
  }
}
</script>

<style scoped lang='scss'>
$action-bar-gap: 0.25rem;
$APGap: 1rem;
$iconSize: 32px;

.c-wro-a-p {
  display: grid!important;

  &__header {
    display: grid;
    grid-template-columns: $APGap $iconSize 6fr 1fr $APGap;
    grid-template-rows: $APGap auto $APGap;
    grid-gap: $APGap;
  }
  &__icon {
    display: flex;
    height: $iconSize;
    grid-area: 2/2/2/2;
    &--incomplete {
      color: #9ea1a7;
    }
  }
  &__title {
    margin-bottom: 0;
    grid-area: 2/3/2/4;
    &--wrap {
      grid-area: 2/3/2/5;
    }
  }
  &__toggle {
    display: flex;
    justify-content: flex-end;
    grid-area: 2/4;
  }

  &__body {
    display: grid;
    grid-template-columns: $APGap $iconSize 2fr 1fr $APGap;
    grid-template-rows: $APGap auto auto auto $action-bar-gap;
    grid-gap: $APGap;
    &--wrap {
      grid-template-rows: $APGap auto auto auto auto $action-bar-gap;
    }
  }
  &__step {
    margin-bottom: 0;
    grid-area: 1/3;
    margin-top: -2rem;
    color: #9ea1a7;
    font-weight: 500;
  }
  &__info {
    grid-area: 1/4/3/4;
    &--wrap {
      grid-area: 2/3/2/5;
    }
  }
  &__content {
    grid-area: 2/3;
    &--wrap {
      grid-area: 3/3/3/5;
    }
  }
  &__table {
    grid-area: 3/1/3/6;
    border-bottom: 1px solid #ecf0f3;
    margin-bottom: $action-bar-gap;
    &--wrap {
      grid-area: 4/1/4/6;
    }
  }
  &__footer {
    grid-area: 4/2/4/5;
    &--wrap {
      grid-area: 5/2/5/5;
    }
  }
}

</style>
